import useMeasurementTypes from "#hooks/useMeasurementTypes";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "#routers/hooks";
import MeasurementsService from "#services/MeasurementsService";
import { NODE_API_MAX_PAGE_SIZE } from "#services/ServiceHelper";
import EditDeviceDialog from "#src/batteries-included-components/Dialogs/EditDeviceDialog";
import { UpdateEntityStatusDialog } from "#src/batteries-included-components/Dialogs/UpdateEntityStatusDialog";
import { DevicesViewFilterArea } from "#src/batteries-included-components/FilterAreas/AssetsFilterAreas";
import { IsFeatureAvailable } from "#src/batteries-included-components/Layouts/Authorization/IsFeatureAvailable";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { useUpdateAssetStatusAsync } from "#src/components/hooks/adapters/useAssets";
import { useListWorkflows } from "#src/components/hooks/adapters/useWorkflows";
import { useCreateEditMetaSegments } from "#src/components/hooks/useCreateEditMetaSegments";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import { DEFAULT_DATE_RANGES } from "#src/hooks/useDateRange";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { DevicesListRoutePath } from "#src/routes/organization/devices";
import { DevicesDetailRoutePath } from "#src/routes/organization/devices/[deviceId]/detail";
import { AssociatedFormSubmissionsTab } from "#src/routes/organization/facilities/[facilityId]/AssociatedFormSubmissionsTab";
import { getAssetFilters } from "#src/routes/organization/facilities/[facilityId]/AssociatedWorkflows.helpers";
import { AssociatedWorkflowsTab } from "#src/routes/organization/facilities/[facilityId]/AssociatedWorkflowsTab";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import { getIntervalOptions } from "#utils/date";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DropdownMenu,
  generateDocumentTitle,
  Icon,
  Page,
  Pill,
  Tab,
  useToast,
} from "@validereinc/common-components";
import {
  DeviceAdapter,
  DeviceType,
  FormSubmissionAdapter,
  isResponseErrorType,
  MeasurementAggregators,
  MeasurementSeriesType,
  ResourceDefinitions,
  Resources,
} from "@validereinc/domain";
import {
  getYearMonthFromDateRange,
  HTTPResponseError,
  toTitleCaseString,
} from "@validereinc/utilities";
import React, { useEffect, useMemo, useState } from "react";
import { Titled } from "react-titled";
import { ChangeLogTab } from "./ChangeLogTab";
import OverviewTab from "./DeviceOverviewTab/DeviceOverviewTab";
import MeasurementsTab from "./MeasurementsTab/MeasurementsTab";
import { fetchMeasurementData, validateFilters } from "./deviceDetailHelper";

const DEFAULT_TAB_KEY = "overview";

const DeviceDetail = () => {
  const navigate = useNavigate();
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isUpdateDeviceStatusDialogOpen, setIsUpdateDeviceStatusDialogOpen] =
    useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { localize } = useLocalization();
  const location = useLocation();
  const [isChangeLogAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "core:activity_logs",
    permissionQuery: "activities:read",
  });
  const [viewConfigStorageKey] = useStorageKey(
    ["view-configuration"],
    "assets-shared"
  );
  const [filters] = useStickyState<{
    period?: { from: string; to: string };
  }>({}, viewConfigStorageKey);
  const { deviceId } = useParams<{ deviceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams<{
    tab: string;
    form_category_name: string;
  }>();
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [deviceDetail, setDeviceDetail] = useState<DeviceType>();
  const [breadcrumbs] = useBreadcrumbsFromRoute(DevicesDetailRoutePath, {
    "/details": {
      title: deviceDetail?.name,
    },
  });
  const [measurementData, setMeasurementData] = useState<any[]>([]);
  const [measurementDataCacheKey, setMeasurementDataCacheKey] = useState("");
  const [areFormsEnabled, isLoadingAreFormsEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:forms",
    permissionQuery: {
      $and: [
        "form_categories:read",
        "form_schemas:read",
        "form_submissions:read",
      ],
    },
  });
  const [areWorkflowsEnabled, isLoadingAreWorkflowsEnabled] =
    useIsFeatureAvailable({
      featureFlagQuery: "core:workflows",
      permissionQuery: {
        $and: ["workflows:read", "workflow_templates:read"],
      },
    });

  const [measurementSources, setMeasurementSources] = useState<
    MeasurementSeriesType[]
  >([]);
  const [assetIdToEdit, setAssetIdToEdit] = useState<string | undefined>();
  const deleteMutation = useMutation({
    mutationFn: (params: Parameters<typeof DeviceAdapter.deleteOne>[0]) => {
      return DeviceAdapter.deleteOne(params);
    },
    onSuccess: (_, params) => {
      toast.push({
        intent: "success",
        description: `Successfully deleted ${localize(Resources.DEVICE)}${params.previousData?.name ? ` ${params.previousData?.name}.` : "."}`,
      });
      queryClient.invalidateQueries({ queryKey: [Resources.DEVICE] });
      navigate(DevicesListRoutePath.toLinkParts());
    },
    onError: (error: any) => {
      if (
        error instanceof HTTPResponseError &&
        error.responseBodyJson &&
        isResponseErrorType(error.responseBodyJson)
      ) {
        toast.push({
          intent: "error",
          description: `Unable to delete ${localize(Resources.DEVICE)} due to a server error: ${error.responseBodyJson.error}`,
        });
      } else {
        toast.push({
          intent: "error",
          description: `Unable to delete ${localize(Resources.DEVICE)}. An unknown error occurred.`,
        });
      }
    },
  });

  const allowedMeasurementTypes = measurementSources.map(
    ({ measurement_type }) => measurement_type
  );

  const [unfilteredMeasurementTypes] = useMeasurementTypes();
  const measurementTypes = useMemo(
    () =>
      unfilteredMeasurementTypes.filter(({ id }: { id: string }) =>
        allowedMeasurementTypes.includes(id)
      ),
    [unfilteredMeasurementTypes, measurementSources]
  );

  const [intervalOptions, setIntervalOptions] = useState(() =>
    getIntervalOptions(DEFAULT_DATE_RANGES.last30Days)
  );

  const [appliedFilters, setAppliedFilters] = useState({
    date_range:
      // @ts-expect-error TODO: fix search param typing
      searchParams?.from && searchParams.to
        ? // @ts-expect-error TODO: fix search param typing
          { from: new Date(searchParams?.from), to: new Date(searchParams.to) }
        : DEFAULT_DATE_RANGES.last30Days,
    // @ts-expect-error TODO: fix search param typing
    interval: searchParams?.interval || intervalOptions?.[0].id,
    // @ts-expect-error TODO: fix search param typing
    measurement_left: searchParams?.measurement_left,
    // @ts-expect-error TODO: fix search param typing
    measurement_right: searchParams?.measurement_right,
    aggregation_function:
      // @ts-expect-error TODO: fix search param typing
      searchParams?.aggregation_function || MeasurementAggregators.AVERAGE,
  });

  const filteredMeasurementData = useMemo(
    () =>
      [appliedFilters.measurement_left, appliedFilters.measurement_right]
        .map((measurementType) =>
          measurementData.find(({ type }) => type === measurementType)
        )
        .filter((value) => value),
    [measurementData, appliedFilters]
  );

  // Provide reasonable intervals based on date range
  useEffect(() => {
    const newIntervals = getIntervalOptions(appliedFilters.date_range);
    setIntervalOptions(newIntervals);

    if (!newIntervals?.map(({ id }) => id)?.includes(appliedFilters.interval)) {
      setAppliedFilters({ ...appliedFilters, interval: newIntervals?.[0].id });
    }
  }, [appliedFilters.date_range]);

  // Fetch Device Detail & Measurement Sources
  useEffect(() => {
    if (deviceId) {
      setIsLoading(true);

      Promise.all([
        DeviceAdapter.getOne({
          id: deviceId,
          meta: { period: getYearMonthFromDateRange(filters?.period) },
        }),
        MeasurementsService.getMeasurementSources({
          deviceId,
          rowPerPage: NODE_API_MAX_PAGE_SIZE,
        }),
      ])
        .then(([{ data: detailData }, { data: sourcesData }]) => {
          setMeasurementSources(sourcesData.data);
          setDeviceDetail(detailData);
          setAppliedFilters(validateFilters(appliedFilters, sourcesData.data));
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [deviceId, filters.period]);
  const createEditMetaSegments = useCreateEditMetaSegments({
    editedAt: deviceDetail?.updated_at,
    createdAt: deviceDetail?.created_at,
  });
  const relatedFormSubmissionsQuery = useQuery({
    queryKey: [Resources.FORM_SUBMISSION, Resources.DEVICE, deviceId],
    queryFn: () => {
      return FormSubmissionAdapter.getList({
        filters: {
          related_entity_id: deviceId,
        },
      });
    },
    enabled: areFormsEnabled && !!deviceId,
  });
  const relatedWorkflowsQuery = useListWorkflows(
    {
      page: 1,
      pageSize: 1,
      filters: {
        ...getAssetFilters(Resources.DEVICE, { assetId: deviceId }),
      },
    },
    {
      enabled: areWorkflowsEnabled && !!deviceId,
    }
  );

  // Fetch Measurements
  useEffect(() => {
    const run = async () => {
      if (!isLoading && measurementSources.length === 0) {
        setIsDataLoading(false);
      }
      if (measurementSources.length && measurementTypes.length) {
        const {
          measurement_left: _measurement_left,
          measurement_right: _measurement_right,
          ...filters
        } = appliedFilters;
        const cacheKey = JSON.stringify(filters);
        // Don't re-fetch data if only the measurement_types have changed
        if (measurementDataCacheKey === cacheKey) {
          return;
        }
        setMeasurementDataCacheKey(cacheKey);

        setIsDataLoading(true);

        const data = await fetchMeasurementData(
          deviceId,
          measurementSources,
          measurementTypes,
          filters
        );

        setMeasurementData(data);
        setIsDataLoading(false);
      }
    };

    run();
  }, [measurementSources, measurementTypes, appliedFilters, isLoading]);

  const [updateDeviceStatusJobStorageKey] = useStorageKey(
    ["async-job"],
    `update-device-status-${deviceId}`
  );

  const updateStatusMutation = useUpdateAssetStatusAsync({
    jobKey: updateDeviceStatusJobStorageKey,
    assetId: deviceId,
    setIsUpdating: setIsUpdatingStatus,
    invalidate: () => {
      queryClient.invalidateQueries({
        queryKey: [
          Resources.DEVICE,
          deviceId,
          { period: getYearMonthFromDateRange(filters?.period) },
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [Resources.ACTIVITY],
      });
    },
  });
  const handleChangeDeviceStatusClick = () =>
    setIsUpdateDeviceStatusDialogOpen(true);

  const onFiltersChange = async (newFilters: any) => {
    if (!newFilters.measurement_left) {
      return;
    }

    setAppliedFilters(newFilters);

    const {
      date_range: { from, to },
      ...restFilters
    } = newFilters;

    navigate({
      pathname: location.pathname,
      query: { ...restFilters, from, to, tab: "measurements" },
      replace: true,
    });
  };

  const onEditDevice = () => setAssetIdToEdit(deviceId);

  const actionRow = [
    <Button
      key="edit"
      // @ts-expect-error TODO: fix button variant typing
      variant="secondary"
      onClick={onEditDevice}
    >
      Edit
    </Button>,
    <>
      <IsFeatureAvailable
        key="change-status-access-policy"
        featureFlagQuery="core:devices"
        permissionQuery="devices:write"
        fallbackChildren={null}
      >
        <Button
          isLoading={isUpdatingStatus}
          onClick={handleChangeDeviceStatusClick}
        >
          Change Status
        </Button>
      </IsFeatureAvailable>
    </>,
    <DropdownMenu
      key="more"
      options={[
        {
          label: "Delete",
          variant: "danger",
          isDisabled: deleteMutation.isLoading,
          slotLeft: ({ iconProps }) => (
            <Icon
              {...iconProps}
              variant="trash"
            />
          ),
          onClick: () => setIsDeleteConfirmationOpen(true),
        },
      ]}
    >
      <Button
        icon="dots-three-vertical"
        variant="outline"
      />
    </DropdownMenu>,
  ];

  const handleActiveTabKeyChange = (tabKey: any) => {
    setSearchParams({ ...searchParams, tab: tabKey });
  };

  const hasAssociatedResources =
    !!relatedWorkflowsQuery.data?.total_entries ||
    !!relatedFormSubmissionsQuery.data?.total_entries;

  return (
    <>
      <Page
        category={localize(DevicesDetailRoutePath.previous?.title ?? "Device")}
        title={
          deviceDetail ? (
            <>
              {deviceDetail.name}{" "}
              <Pill
                variant={
                  deviceDetail.status === "active" ? "success" : "default"
                }
                hasDot={false}
              >
                {toTitleCaseString(deviceDetail.status, {
                  intent: "ui",
                  exceptions: ["oil-and-gas"],
                })}
              </Pill>
            </>
          ) : (
            DevicesDetailRoutePath.title
          )
        }
        isLoading={
          isLoading || isLoadingAreFormsEnabled || isLoadingAreWorkflowsEnabled
        }
        breadcrumbs={breadcrumbs}
        actionRow={actionRow}
        onActiveTabKeyChange={handleActiveTabKeyChange}
        activeTabKey={
          !isChangeLogAvailable && searchParams?.tab === "change-log"
            ? DEFAULT_TAB_KEY
            : (searchParams?.tab ?? DEFAULT_TAB_KEY)
        }
        renderMeta={
          createEditMetaSegments.length === 0
            ? undefined
            : ({ MetaSegments }) => (
                <MetaSegments values={createEditMetaSegments} />
              )
        }
      >
        <Tab
          tabKey="overview"
          title="Overview"
        >
          <Titled
            title={generateDocumentTitle(
              breadcrumbs.at(-1)?.title ?? DevicesDetailRoutePath.title,
              localize("device_plural")
            )}
          >
            <OverviewTab
              isLoading={isLoading}
              deviceDetail={deviceDetail}
              viewFilterAreaSlot={
                <DevicesViewFilterArea
                  viewConfigStorageKey={viewConfigStorageKey}
                />
              }
            />
          </Titled>
        </Tab>
        <Tab
          tabKey="measurements"
          title={ResourceDefinitions.measurement.label.plural}
        >
          <Titled
            title={generateDocumentTitle(
              `${
                breadcrumbs.at(-1)?.title ?? DevicesDetailRoutePath.title
              } ${ResourceDefinitions.measurement.label.plural}`,
              breadcrumbs.at(-2)?.title
            )}
          >
            <MeasurementsTab
              appliedFilters={appliedFilters}
              onFiltersChange={onFiltersChange}
              measurementTypes={measurementTypes}
              intervalOptions={intervalOptions}
              filteredMeasurementData={filteredMeasurementData}
              isDataLoading={isDataLoading}
              measurementSources={measurementSources}
            />
          </Titled>
        </Tab>

        {areFormsEnabled ? (
          <Tab
            tabKey={Resources.FORM_SUBMISSION}
            title="Forms"
          >
            <Titled
              title={generateDocumentTitle(
                `${
                  breadcrumbs.at(-1)?.title ?? DevicesDetailRoutePath.title
                } ${ResourceDefinitions.form_submission.label.plural}`,
                breadcrumbs.at(-2)?.title
              )}
            >
              <AssociatedFormSubmissionsTab
                resourceId={deviceId}
                resourceType={Resources.DEVICE}
                defaultFormCategoryName={searchParams.form_category_name}
              />
            </Titled>
          </Tab>
        ) : null}

        {areWorkflowsEnabled ? (
          <Tab
            tabKey={Resources.WORKFLOW}
            title={ResourceDefinitions.workflow.label.plural}
          >
            <Titled
              title={generateDocumentTitle(
                `${
                  breadcrumbs.at(-1)?.title ?? DevicesDetailRoutePath.title
                } ${ResourceDefinitions.workflow.label.plural}`,
                breadcrumbs.at(-2)?.title
              )}
            >
              <AssociatedWorkflowsTab
                resourceId={deviceId}
                resourceType={Resources.DEVICE}
              />
            </Titled>
          </Tab>
        ) : null}

        <Tab
          tabKey="change-log"
          title={ResourceDefinitions.activity.label.singular}
          isDisabled={!isChangeLogAvailable}
          iconVariant={!isChangeLogAvailable ? "lock" : undefined}
        >
          <Titled
            title={generateDocumentTitle(
              `${
                breadcrumbs.at(-1)?.title ?? DevicesDetailRoutePath.title
              } ${ResourceDefinitions.activity.label.plural}`,
              breadcrumbs.at(-2)?.title
            )}
          >
            {isChangeLogAvailable ? (
              <ChangeLogTab resourceId={deviceId} />
            ) : null}
          </Titled>
        </Tab>
      </Page>

      <Dialog
        isOpen={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
        title={`Delete ${toTitleCaseString(localize(Resources.DEVICE), { intent: "ui" })}`}
        actionRow={[
          <Button
            key="confirm"
            variant="error"
            onClick={() =>
              deleteMutation.mutate({
                id: deviceId,
                previousData: deviceDetail,
              })
            }
            isLoading={deleteMutation.isLoading}
            disabled={hasAssociatedResources}
          >
            Delete
          </Button>,
        ]}
      >
        {hasAssociatedResources ? (
          <>
            You cannot delete {deviceDetail?.name} until the following
            associated resources are deleted:
            <ul style={{ listStyle: "none", padding: 0, marginBlock: 8 }}>
              {relatedWorkflowsQuery.data?.total_entries ? (
                <li>
                  <RoutingLink
                    to={DevicesDetailRoutePath.toLinkParts({
                      pathParams: { deviceId },
                      queryParams: { tab: Resources.WORKFLOW },
                    })}
                    onClickCapture={() => setIsDeleteConfirmationOpen(false)}
                  >
                    {relatedWorkflowsQuery.data?.total_entries} Workflows
                  </RoutingLink>
                </li>
              ) : null}
              {relatedFormSubmissionsQuery.data?.total_entries ? (
                <li>
                  <RoutingLink
                    to={DevicesDetailRoutePath.toLinkParts({
                      pathParams: { deviceId },
                      queryParams: { tab: Resources.FORM_SUBMISSION },
                    })}
                    onClickCapture={() => setIsDeleteConfirmationOpen(false)}
                  >
                    {relatedFormSubmissionsQuery.data?.total_entries} Form
                    Submissions
                  </RoutingLink>
                </li>
              ) : null}
            </ul>
            Please delete them and return here to delete this{" "}
            {localize(Resources.DEVICE)}.
          </>
        ) : (
          <>
            Are you sure you want to delete this {localize(Resources.DEVICE)}?
            This will also delete its associated Measurements. This action
            cannot be undone.
          </>
        )}
      </Dialog>

      <EditDeviceDialog
        onClose={() => setAssetIdToEdit(undefined)}
        deviceId={assetIdToEdit}
      />
      <UpdateEntityStatusDialog
        isOpen={isUpdateDeviceStatusDialogOpen}
        onClose={() => {
          setIsUpdateDeviceStatusDialogOpen(false);
        }}
        onSubmit={updateStatusMutation.handleUpdateAssetStatusAsync}
        itemCount={1}
        subject={{
          singular: ResourceDefinitions.device.label.singular,
          plural: ResourceDefinitions.device.label.plural,
        }}
        warningMessage="This will inactivate all associated estimation methods. Inactivating estimation methods will delete any associated results for the provided time range."
      />
    </>
  );
};

export default DeviceDetail;
